import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import { FormWithTableItem, GenericInput } from '../../../components/common';
import selectConstant from '../../../utils/selectConstant';
import { getAddressesForm, validate } from '../../../utils/utils';
import { countryList } from '../../../constantsApp/countryList';
import {
  applyTenantFieldConfig,
  applyRegionOptions,
  getRegionResets,
  fillOptionalBlanks,
} from '../../../utils/tenantFieldConfig';

const dataFieldsContact = [
  {
    name: 'email',
    label: 'common:label.email',
    isNoRequired: true,
  },
  {
    name: 'firstName',
    label: 'common:label.firstName',
  },
  {
    name: 'middleName',
    label: 'common:label.middleName',
    isNoRequired: true,
    optIn: true,
  },
  {
    name: 'lastName',
    label: 'common:label.lastName',
    isNoRequired: true,
  },
  {
    name: 'identityDocument',
    label: 'common:label.identityDocument',
    isNoRequired: true,
    optIn: true,
  },
];

export const dataField = {
  withAddressesUSForm: [
    {
      name: 'street',
      label: 'common:label.street',
      type: 'textarea',
    },
    {
      label: 'common:label.country',
      name: 'country',
      options: countryList,
      type: 'select',
    },
    {
      label: 'common:label.state',
      name: 'state',
      options: selectConstant.stateUSData,
      isCreateOption: true,
      type: 'select',
    },
    {
      name: 'district',
      label: 'common:label.district',
      isNoRequired: true,
      optIn: true,
    },
    {
      label: 'common:label.city',
      name: 'city',
      options: [],
      isCreateOption: true,
      type: 'select',
      fieldSelect: 'city',
    },
    {
      label: 'common:label.postalCode',
      name: 'postalCode',
      options: [],
      isCreateOption: true,
      type: 'select',
      fieldSelect: 'postalCode',
    },
  ],

  withAddressesNormalForm: [
    {
      name: 'street',
      label: 'common:label.street',
      type: 'textarea',
    },
    {
      label: 'common:label.country',
      name: 'country',
      options: countryList,
      type: 'select',
    },
    {
      name: 'state',
      label: 'common:label.state',
    },
    {
      name: 'district',
      label: 'common:label.district',
      isNoRequired: true,
      optIn: true,
    },
    {
      label: 'common:label.city',
      name: 'city',
    },
    {
      name: 'postalCode',
      label: 'common:label.postalCode',
    },
  ],
};

// The tenant's required/optional settings win over the screen's own isNoRequired.
const isRequired = val => (val.required !== undefined ? val.required : !val.isNoRequired);

const QuickCreate = ({
  data,
  createAccount,
  modeCreateCustomer,
  ccpPropertyList,
  addressRegions,
  loadAddressRegions,
}) => {
  const formRef = useRef();

  const { t, i18n } = useTranslation('common');
  const language = i18n && i18n.language;

  const [dataSubmit, setDataSubmit] = useState({});
  const [wasValidated, setValidate] = useState(false);

  const onChangeData = ({ name, value, field }) => {
    if (!dataSubmit[field]) dataSubmit[field] = [{}];
    // A new province/canton invalidates the region levels below it; a new country, all of them.
    Object.assign(
      dataSubmit[field][0],
      { [name]: value },
      field === 'addresses' ? getRegionResets(ccpPropertyList, name, value, dataSubmit[field][0]) : {}
    );
    setDataSubmit({ ...dataSubmit });
    if (field === 'addresses' && name === 'country' && loadAddressRegions) loadAddressRegions(value);
  };

  const onSubmit = evt => {
    evt.preventDefault();
    setValidate(true);
    if (!validate(true, formRef, t)) {
      return false;
    }
    const newPayload = cloneDeep(dataSubmit);
    newPayload.contacts = newPayload.contacts.map(val => {
      const { phones, ...rest } = val;
      return rest;
    });
    // A tenant with optional fields sends each blank one as '' and a blank country as its defaultCountry
    newPayload.contacts = newPayload.contacts.map(contact => fillOptionalBlanks(contact, ccpPropertyList, 'contact'));
    if (newPayload.addresses) {
      newPayload.addresses = newPayload.addresses.map(address => fillOptionalBlanks(address, ccpPropertyList, 'address'));
    }
    return createAccount(newPayload);
  };

  const onResetData = useCallback(() => {
    setDataSubmit(cloneDeep(data));
  }, [data]);

  useEffect(() => {
    onResetData();
  }, [onResetData]);

  const contactData = dataSubmit && dataSubmit.contacts ? dataSubmit.contacts[0] : {};
  const contactAddress = dataSubmit && dataSubmit.addresses ? dataSubmit.addresses[0] : {};
  let listFields = dataField.withAddressesNormalForm;

  if (contactAddress && contactAddress.country === 'USA') {
    listFields = getAddressesForm({
      city: contactAddress.city,
      state: contactAddress.state,
      country: contactAddress.country,
      addresses: dataField,
    });
  }
  const contactFields = applyTenantFieldConfig({
    dataField: dataFieldsContact,
    ccpPropertyList,
    prefix: 'contact',
    language,
    scopedOnly: true,
  });
  listFields = applyRegionOptions({
    dataField: applyTenantFieldConfig({
      dataField: listFields,
      ccpPropertyList,
      prefix: 'address',
      language,
      scopedOnly: true,
    }),
    ccpPropertyList,
    regions: (addressRegions && contactAddress && addressRegions[contactAddress.country]) || null,
    data: contactAddress,
  });

  if (modeCreateCustomer !== 2) return null;

  return (
    <form
      ref={formRef}
      noValidate
      className={`col-md-12 mb-30 needs-validation ${wasValidated ? 'was-validated' : ''}`}
      onSubmit={onSubmit}
    >
      <FormWithTableItem title={t('label.quickCreateAccount')}>
        <div className="mb-4 ml-3 mr-3 mt-3 col-md-12 row">
          {contactFields.map(val => (
            <GenericInput
              {...val}
              required={isRequired(val)}
              value={contactData[val.name] || null}
              onChange={e => onChangeData({ ...e, field: 'contacts' })}
            />
          ))}
          {listFields.map(val => {
            const { optionSelect, options, required, isNoRequired, ...rest } = val;
            return (
              <GenericInput
                options={val.optionSelect || val.options || []}
                required={isRequired(val)}
                value={contactAddress[val.name] || null}
                onChange={e => onChangeData({ ...e, field: 'addresses' })}
                {...rest}
                type={optionSelect ? 'select' : rest.type}
              />
            );
          })}
        </div>
        <div className="col-sm-12 mb-0 btn-account pb-3">
          {modeCreateCustomer === 2 && (
            <button
              type="submit"
              className="button x-small float-right"
              // onClick={onHandleSubmit}
            >
              {t('common:label.createAccount')}
            </button>
          )}
          <button type="button" className="button mr-2 button-border black x-small float-right" onClick={onResetData}>
            {t('common:label.cancel')}
          </button>
        </div>
      </FormWithTableItem>
    </form>
  );
};

QuickCreate.propTypes = {
  modeCreateCustomer: PropTypes.number,
  createAccount: PropTypes.func.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  ccpPropertyList: PropTypes.arrayOf(PropTypes.object),
  addressRegions: PropTypes.objectOf(PropTypes.any),
  loadAddressRegions: PropTypes.func,
};

QuickCreate.defaultProps = {
  data: {},
  modeCreateCustomer: 0,
  ccpPropertyList: [],
  addressRegions: null,
  loadAddressRegions: null,
};

export default QuickCreate;
